/**
 * speciesClass — the "what KIND of Pokémon is this" axis for the Dex grid
 * (2026-09-01 owner ask: "add a filter to dex for starter / legendary /
 * mythical / ultra beast / pseudo-legendary").
 *
 * Every other Dex filter answers a question about the roster (region, type) or
 * about the trainer (caught, released). This one answers the question trainers
 * actually plan around: the classes are what a dex gap FEELS like. A missing
 * starter is a trade or a research task, a missing Legendary is a raid, a
 * missing Mythical is a ticket, an Ultra Beast is a rotation, a
 * pseudo-legendary is a candy grind. Nothing in the app could express any of
 * them before.
 *
 * WHERE EACH ROSTER COMES FROM
 *   - legendary / mythical: the existing curated id lists (legendaryDexNumbers,
 *     mythicalDexNumbers), which are already the app's authority for those two
 *     classes. They are deliberately disjoint from each other, and both scope
 *     Ultra Beasts out, so the three sets here cannot double-count.
 *   - ultra beast: the eleven UBs, curated here. `lib/pokemon.ts` carries an
 *     `ultraBeast` flag but only on a ~120-entry sample, so it is a sample, not
 *     a roster (it is missing Naganadel outright).
 *   - starter / pseudo-legendary: curated here, for the same reason as the
 *     other two — no dataset in the app carries either concept.
 *
 * TWO DELIBERATE SHAPE DECISIONS, because they are the ones that look
 * inconsistent until you know why:
 *
 *   STARTERS ARE THE BASE TRIO ONLY (Bulbasaur / Charmander / Squirtle, not
 *   Venusaur). That is the ask as stated ("first 3 base grass/water/fire in
 *   each region"), and it is also the useful set: a starter is the thing you
 *   are given or hatch, and its evolutions are a candy problem rather than a
 *   catch problem. A trainer who wants the whole lines has the Stages axis
 *   ("Whole line") sitting one row above in the same menu, and crossing the
 *   two gives all 27 Kanto-to-Paldea starter-line members.
 *
 *   PSEUDO-LEGENDARIES ARE THE WHOLE FAMILY (Dratini AND Dragonair AND
 *   Dragonite). "Pseudo-legendary" names a 600-base-stat FINAL evolution, so a
 *   filter that offered Dratini and hid Dragonite would be filtering out the
 *   very species the word refers to.
 *
 * Hisui has no starters of its own: Rowlet, Cyndaquil and Oshawott are Alola,
 * Johto and Unova starters that Hisui borrows, and they are counted once, in
 * the region that first shipped them.
 */
import { isLegendaryDex } from "./legendaryDexNumbers";
import { isMythicalDex } from "./mythicalDexNumbers";
import { isBabyDex } from "./babyDexNumbers";

/** The classes the Dex filter offers, in the order its chips render. */
export type SpeciesClass =
  | "starter"
  | "baby"
  | "legendary"
  | "mythical"
  | "ultra-beast"
  | "pseudo";

export const SPECIES_CLASS_ORDER: SpeciesClass[] = [
  "starter",
  "baby",
  "legendary",
  "mythical",
  "ultra-beast",
  "pseudo",
];

export const SPECIES_CLASS_LABELS: Record<SpeciesClass, string> = {
  starter: "Starter",
  // The nineteen pre-evolutions (Pichu, Cleffa, Togepi …), see
  // lib/babyDexNumbers. 2026-09-09 owner ask.
  baby: "Baby",
  legendary: "Legendary",
  mythical: "Mythical",
  "ultra-beast": "Ultra Beast",
  pseudo: "Pseudo",
};

/**
 * The base form of every regional starter trio, grass / fire / water in each
 * region's own dex order.
 */
export const STARTER_DEX_NUMBERS: readonly number[] = [
  1, 4, 7,          // Kanto:  Bulbasaur, Charmander, Squirtle
  152, 155, 158,    // Johto:  Chikorita, Cyndaquil, Totodile
  252, 255, 258,    // Hoenn:  Treecko, Torchic, Mudkip
  387, 390, 393,    // Sinnoh: Turtwig, Chimchar, Piplup
  495, 498, 501,    // Unova:  Snivy, Tepig, Oshawott
  650, 653, 656,    // Kalos:  Chespin, Fennekin, Froakie
  722, 725, 728,    // Alola:  Rowlet, Litten, Popplio
  810, 813, 816,    // Galar:  Grookey, Scorbunny, Sobble
  906, 909, 912,    // Paldea: Sprigatito, Fuecoco, Quaxly
];

/**
 * The eleven Ultra Beasts. Poipole / Naganadel are included even though the
 * pair arrived in GO later than the rest: this is a taxonomy, not a release
 * roster, and the Released axis is what answers "can I catch it yet".
 */
export const ULTRA_BEAST_DEX_NUMBERS: readonly number[] = [
  793, // Nihilego
  794, // Buzzwole
  795, // Pheromosa
  796, // Xurkitree
  797, // Celesteela
  798, // Kartana
  799, // Guzzlord
  803, // Poipole
  804, // Naganadel
  805, // Stakataka
  806, // Blacephalon
];

/**
 * The ten pseudo-legendary lines, every stage. Each is a three-stage line whose
 * final form totals 600 base stats.
 */
export const PSEUDO_LEGENDARY_DEX_NUMBERS: readonly number[] = [
  147, 148, 149, // Dratini    -> Dragonite
  246, 247, 248, // Larvitar   -> Tyranitar
  371, 372, 373, // Bagon      -> Salamence
  374, 375, 376, // Beldum     -> Metagross
  443, 444, 445, // Gible      -> Garchomp
  633, 634, 635, // Deino      -> Hydreigon
  704, 705, 706, // Goomy      -> Goodra
  782, 783, 784, // Jangmo-o   -> Kommo-o
  885, 886, 887, // Dreepy     -> Dragapult
  996, 997, 998, // Frigibax   -> Baxcalibur
];

const STARTERS = new Set<number>(STARTER_DEX_NUMBERS);
const ULTRA_BEASTS = new Set<number>(ULTRA_BEAST_DEX_NUMBERS);
const PSEUDOS = new Set<number>(PSEUDO_LEGENDARY_DEX_NUMBERS);

/** True if the species belongs to the given class. */
export function isSpeciesClass(cls: SpeciesClass, dexNumber: number): boolean {
  switch (cls) {
    case "starter":
      return STARTERS.has(dexNumber);
    case "baby":
      return isBabyDex(dexNumber);
    case "legendary":
      return isLegendaryDex(dexNumber);
    case "mythical":
      return isMythicalDex(dexNumber);
    case "ultra-beast":
      return ULTRA_BEASTS.has(dexNumber);
    case "pseudo":
      return PSEUDOS.has(dexNumber);
  }
}

/**
 * Filter predicate: an empty selection is "any class", and a non-empty one is
 * an OR across the picked classes.
 *
 * OR rather than AND for the same reason the Type axis is: the classes are
 * mutually exclusive by construction, so an AND could only ever produce an
 * empty grid, while an OR answers the question people actually have ("show me
 * the rare stuff": Legendary + Mythical + Ultra Beast at once).
 */
export function matchesSpeciesClasses(
  selected: readonly SpeciesClass[] | undefined,
  dexNumber: number,
): boolean {
  if (!selected || selected.length === 0) return true;
  return selected.some((c) => isSpeciesClass(c, dexNumber));
}
